<template>
  <div class="tp" :class="{ 'tp-own': !!audio, 'tp-invite': invite, 'tp-playing': playing }">
    <template v-if="audio">
      <div ref="bar" class="tp-bar" data-first-view="player">
        <button type="button" class="tp-play" :aria-label="playLabel" @click="toggle">
          <BrandIcon :name="playing ? 'stop' : 'run'" />
        </button>
        <div class="tp-track">
          <div class="tp-row">
            <p class="tp-status" aria-live="polite"><BrandIcon v-if="playing" name="play" class="tp-eq" />{{ status }}</p>
            <label class="tp-auto"><input type="checkbox" :checked="autoOn" @change="setAuto($event.target.checked)" /><span>{{ zh ? '進頁自動播放' : 'Autoplay' }}</span></label>
          </div>
          <input class="tp-seek" type="range" min="0" :max="total" step="0.1" :value="current" :style="{ '--p': pct + '%' }" :aria-label="zh ? '播放進度' : 'Playback position'" :aria-valuetext="clock(current) + ' / ' + clock(total)" @input="seek" />
          <p class="tp-time"><span>{{ clock(current) }}</span><span>{{ clock(total) }}</span></p>
        </div>
      </div>
    </template>
    <template v-else>
      <iframe class="tp-embed" data-first-view="player" :src="embedUrl" :title="(zh ? 'StreetVoice 播放器：' : 'StreetVoice player: ') + original" width="330" height="100" loading="lazy" allow="encrypted-media"></iframe>
      <p class="tp-note">{{ zh ? '這首還沒放上站內播放器，先用 StreetVoice 的，按下才會播。' : 'This piece is not on the site player yet; StreetVoice’s player plays it when you press play.' }}</p>
    </template>

    <div v-if="mini" class="tp-mini" :style="{ '--p': pct + '%' }">
      <button type="button" class="tp-play tp-play-mini" :aria-label="playLabel" @click="toggle">
        <BrandIcon :name="playing ? 'stop' : 'run'" />
      </button>
      <p class="tp-mini-title">{{ title }}</p>
      <p class="tp-mini-time">{{ clock(current) }} / {{ clock(total) }}</p>
    </div>

    <aside v-if="upNext" class="tp-next" :class="{ 'tp-next-raised': mini }" :aria-label="zh ? '下一首' : 'Next piece'">
      <img v-if="next.cover" :src="next.cover" alt="" width="56" height="56" />
      <div class="tp-next-text">
        <p class="tp-next-label" aria-live="polite">{{ zh ? `下一首，${countdown} 秒後播放` : `Next piece in ${countdown} s` }}</p>
        <p class="tp-next-title">{{ next.title }}</p>
      </div>
      <div class="tp-next-actions">
        <button type="button" class="tp-next-go" @click="goNext">{{ zh ? '現在聽' : 'Play now' }}</button>
        <button type="button" class="tp-next-cancel" @click="cancelNext">{{ zh ? '取消' : 'Cancel' }}</button>
      </div>
    </aside>
  </div>
</template>
<script setup>
// 單曲頁自己的播放器（2026-10-04；哲宇：「希望第一眼要能看完詩跟封面跟可以聽音樂 然後 有辦法讓他進去自動播放嗎？」）。
// 有站內音檔（src/data/music-audio.json）才用自己的；沒有就退回 StreetVoice 嵌入播放器，放在同一個位置。
// 進頁自動播放：這份文件已經有使用者手勢（站內點進來）才播；被瀏覽器擋下不報錯，播放鍵改成「點一下開始聽」。
// 規則與為什麼 → docs/MUSIC.md §播放器
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import { trackEvent } from '@/runtime/analytics.mjs'
import BrandIcon from '@/components/BrandIcon.vue'
import { sharedAudio, markContinue, takeContinue, autoplayPreference, setAutoplayPreference, shouldAutoplay } from './shared-audio.mjs'

const props = defineProps({
  slug: { type: String, required: true },
  title: { type: String, required: true },
  original: { type: String, required: true },
  seconds: { type: Number, default: 0 },
  embedUrl: String,
  audio: Object,            // { src, seconds }；沒有＝退回 StreetVoice
  artwork: { type: Array, default: () => [] },
  next: Object,             // { title, path, slug, cover }：時間序的下一首，與頁尾一致
  zh: Boolean,
})
const router = useRouter()
const bar = ref(null)
const playing = ref(false), started = ref(false), ended = ref(false), current = ref(0), duration = ref(0)
const autoOn = ref(true)
const mini = ref(false)
const upNext = ref(false), countdown = ref(8)
let el = null, timer = null, observer = null, autoplayed = false, reported = false

const total = computed(() => duration.value || props.audio?.seconds || props.seconds || 0)
const pct = computed(() => (total.value ? Math.min(100, (current.value / total.value) * 100) : 0))
const invite = computed(() => !!props.audio && !started.value && !playing.value)
const playLabel = computed(() => props.zh ? (playing.value ? `暫停〈${props.title}〉` : `播放〈${props.title}〉`) : (playing.value ? `Pause ${props.title}` : `Play ${props.title}`))
const status = computed(() => {
  if (playing.value) return props.zh ? '播放中' : 'Playing'
  if (ended.value) return props.zh ? '聽完了' : 'Finished'
  if (started.value) return props.zh ? '暫停中' : 'Paused'
  return props.zh ? '點一下開始聽' : 'Tap to listen'
})
const clock = s => { s = Math.max(0, Math.floor(s || 0)); return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}` }
const mine = () => el && el.dataset.slug === props.slug

function play() {
  if (!el) return Promise.resolve(false)
  return el.play().then(() => true, () => false)   // 被瀏覽器擋下：不報錯，留在「點一下開始聽」
}
function toggle() {
  if (!el) return
  cancelNext()
  if (el.paused) { autoplayed = false; play() } else el.pause()
}
function seek(e) {
  if (!el) return
  const v = Number(e.target.value)
  current.value = v
  try { el.currentTime = v } catch { /* metadata 還沒到 */ }
}
function setAuto(on) { autoOn.value = on; setAutoplayPreference(on) }

function goNext() {
  if (!props.next) return
  clearInterval(timer); timer = null
  markContinue(props.next.slug)
  router.push(props.next.path)
}
function cancelNext() { clearInterval(timer); timer = null; upNext.value = false }
function startCountdown() {
  if (!props.next) return
  countdown.value = 8; upNext.value = true
  clearInterval(timer)
  timer = setInterval(() => { countdown.value -= 1; if (countdown.value <= 0) goNext() }, 1000)
}

const on = {
  play: () => { playing.value = true; started.value = true; ended.value = false; if (!reported) { reported = true; trackEvent('music_play', { track: props.original, autoplay: autoplayed }) } session() },
  pause: () => { playing.value = false },
  timeupdate: () => { current.value = el.currentTime },
  durationchange: () => { if (Number.isFinite(el.duration)) duration.value = el.duration },
  ended: () => { playing.value = false; ended.value = true; current.value = total.value; trackEvent('music_complete', { track: props.original }); startCountdown() },
}

// Media Session：鎖定畫面與耳機鍵看到歌名、封面；播放／暫停／下一首
function session(clear) {
  const ms = typeof navigator !== 'undefined' && navigator.mediaSession
  if (!ms) return
  try {
    if (clear) { for (const a of ['play', 'pause', 'nexttrack']) ms.setActionHandler(a, null); ms.metadata = null; return }
    if (typeof MediaMetadata !== 'undefined') ms.metadata = new MediaMetadata({ title: props.title, artist: '吳哲宇 Che-Yu Wu', artwork: props.artwork })
    ms.setActionHandler('play', () => play())
    ms.setActionHandler('pause', () => el?.pause())
    ms.setActionHandler('nexttrack', props.next ? goNext : null)
  } catch { /* 舊瀏覽器不支援某個動作 */ }
}

// 空白鍵：焦點不在輸入框或按鈕上時切換播放（按鈕自己會吃空白鍵，避免按一次切兩次）
function onKey(e) {
  if (e.key !== ' ' && e.code !== 'Space') return
  if (e.defaultPrevented || e.repeat || e.altKey || e.ctrlKey || e.metaKey) return
  const t = e.target
  if (t && t.closest && t.closest('input, textarea, select, button, a, summary, iframe, [contenteditable=""], [contenteditable="true"], [role="slider"], [role="dialog"]')) return
  e.preventDefault()
  toggle()
}

onMounted(() => {
  autoOn.value = autoplayPreference()
  const continued = takeContinue(props.slug)
  if (!props.audio) return
  el = sharedAudio()
  if (!el) return
  if (el.dataset.slug !== props.slug) {
    el.pause()
    el.src = props.audio.src
    el.dataset.slug = props.slug
    el.load()
  }
  for (const [k, fn] of Object.entries(on)) el.addEventListener(k, fn)
  playing.value = !el.paused; current.value = el.currentTime || 0
  if (Number.isFinite(el.duration)) duration.value = el.duration
  window.addEventListener('keydown', onKey)
  if (typeof IntersectionObserver !== 'undefined' && bar.value) {
    observer = new IntersectionObserver(([entry]) => { mini.value = !entry.isIntersecting && entry.boundingClientRect.bottom < 0 })
    observer.observe(bar.value)
  }
  if (!el.paused) return
  if (shouldAutoplay({ continued, preference: autoOn.value })) { autoplayed = true; play() }
})

onBeforeUnmount(() => {
  clearInterval(timer)
  observer?.disconnect()
  window.removeEventListener('keydown', onKey)
  if (!el) return
  for (const [k, fn] of Object.entries(on)) el.removeEventListener(k, fn)
  // 離開這一頁就停（換到下一首時，新的一頁會接手同一個 <audio>）
  if (mine()) { el.pause(); session(true) }
})
</script>
<style>
.tp {
  --tp-paper: #f2eee5;
  --tp-muted: #b8b5ad;
  --tp-line: rgba(242, 238, 229, 0.18);
}
.tp-bar {
  display: flex;
  align-items: center;
  gap: 16px;
}
.tp-play {
  flex: none;
  display: grid;
  place-items: center;
  width: 60px;
  height: 60px;
  border: 0;
  border-radius: 50%;
  background: var(--tp-paper);
  color: #080808;
  cursor: pointer;
  transition: background-color 0.2s ease, transform 0.2s ease;
}
/* 2026-10-04 播放／暫停改用 2023 品牌圖示 Run（空心三角）／Stop（雙豎條）；播放中，狀態字前多一個 Play（等化器，五條會跳） */
.tp-play .brand-icon { width: 30px; height: 30px; }
.tp-play.tp-play-mini .brand-icon { width: 22px; height: 22px; }
.tp .tp-status .tp-eq { width: 16px; height: 16px; margin-right: 6px; vertical-align: -3px; color: var(--gold, #d9c267); }
@media (prefers-reduced-motion: no-preference) {
  .tp-eq path { transform-box: fill-box; transform-origin: 50% 100%; animation: tp-eq 1.1s ease-in-out infinite alternate; }
  .tp-eq path:nth-child(2) { animation-delay: -.35s; }
  .tp-eq path:nth-child(3) { animation-delay: -.7s; }
  .tp-eq path:nth-child(4) { animation-delay: -.2s; }
  .tp-eq path:nth-child(5) { animation-delay: -.55s; }
}
@keyframes tp-eq { from { transform: scaleY(1); } to { transform: scaleY(.45); } }
.tp-play:hover {
  background: var(--gold, #d9c267);
}
.tp-play:active {
  transform: scale(0.96);
}
.tp-play:focus-visible,
.tp-seek:focus-visible,
.tp-auto input:focus-visible,
.tp-next button:focus-visible {
  outline: 2px solid var(--gold, #d9c267);
  outline-offset: 4px;
}
/* 還沒開始聽（自動播放被擋、或開關關著）：播放鍵換成金色並帶一圈呼吸，旁邊寫「點一下開始聽」 */
.tp-invite .tp-bar .tp-play {
  background: var(--gold, #d9c267);
  box-shadow: 0 0 0 0 rgba(217, 194, 103, 0.55);
  animation: tp-breathe 2.4s ease-out infinite;
}
.tp-invite .tp-status {
  color: var(--gold, #d9c267);
}
@keyframes tp-breathe {
  0% { box-shadow: 0 0 0 0 rgba(217, 194, 103, 0.5); }
  70% { box-shadow: 0 0 0 14px rgba(217, 194, 103, 0); }
  100% { box-shadow: 0 0 0 0 rgba(217, 194, 103, 0); }
}
@media (prefers-reduced-motion: reduce) {
  .tp-invite .tp-bar .tp-play { animation: none; box-shadow: 0 0 0 3px rgba(217, 194, 103, 0.45); }
}
.tp-track {
  flex: 1;
  min-width: 0;
}
.tp-row {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
}
.tp .tp-status {
  margin: 0 0 2px;
  font-size: 13px;
  line-height: 1.4;
  color: var(--tp-muted);
}
.tp .tp-time {
  display: flex;
  justify-content: space-between;
  margin: 2px 0 0;
  font-size: 12px;
  font-variant-numeric: tabular-nums;
  color: var(--tp-muted);
}
/* 進度條：原生 range（拖曳、鍵盤左右鍵都免費），只換皮 */
.tp-seek {
  -webkit-appearance: none;
  appearance: none;
  display: block;
  width: 100%;
  height: 22px;
  margin: 0;
  background: transparent;
  cursor: pointer;
}
.tp-seek::-webkit-slider-runnable-track {
  height: 3px;
  border-radius: 2px;
  background: linear-gradient(to right, var(--tp-paper) var(--p, 0%), var(--tp-line) var(--p, 0%));
}
.tp-seek::-moz-range-track {
  height: 3px;
  border-radius: 2px;
  background: linear-gradient(to right, var(--tp-paper) var(--p, 0%), var(--tp-line) var(--p, 0%));
}
.tp-seek::-webkit-slider-thumb {
  -webkit-appearance: none;
  width: 13px;
  height: 13px;
  margin-top: -5px;
  border: 0;
  border-radius: 50%;
  background: var(--tp-paper);
}
.tp-seek::-moz-range-thumb {
  width: 13px;
  height: 13px;
  border: 0;
  border-radius: 50%;
  background: var(--tp-paper);
}
.tp-auto {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: var(--tp-muted);
  cursor: pointer;
}
.tp-auto input {
  width: 14px;
  height: 14px;
  margin: 0;
  accent-color: var(--gold, #d9c267);
}
.tp-embed {
  display: block;
  width: 100%;
  max-width: 360px;
  height: 100px;
  border: 0;
  border-radius: 8px;
  background: #fff;
}
.tp .tp-note {
  margin: 8px 0 0;
  font-size: 12px;
  line-height: 1.5;
  color: var(--tp-muted);
}
/* 手機：捲過播放列後，底部固定一條迷你播放列（桌機左欄本來就看得到，不出現） */
.tp-mini {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 40;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 18px calc(10px + env(safe-area-inset-bottom, 0px));
  background: rgba(12, 12, 12, 0.94);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border-top: 1px solid var(--tp-line);
}
.tp-mini::before {
  content: '';
  position: absolute;
  top: -1px;
  left: 0;
  height: 2px;
  width: var(--p, 0%);
  background: var(--gold, #d9c267);
}
.tp-play.tp-play-mini {
  width: 40px;
  height: 40px;
}
.tp .tp-mini-title {
  flex: 1;
  min-width: 0;
  margin: 0;
  font-family: var(--font-display);
  font-size: 15px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: var(--tp-paper);
}
.tp .tp-mini-time {
  margin: 0;
  font-size: 12px;
  font-variant-numeric: tabular-nums;
  color: var(--tp-muted);
}
@media (min-width: 992px) {
  .tp-mini { display: none; }
}
/* 播完：下一首卡片＋8 秒倒數 */
.tp-next {
  position: fixed;
  right: 24px;
  bottom: 24px;
  z-index: 41;
  display: flex;
  align-items: center;
  gap: 14px;
  width: min(420px, calc(100vw - 32px));
  padding: 14px 16px;
  background: #16161a;
  border: 1px solid var(--tp-line);
  border-radius: 10px;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.5);
}
.tp-next img {
  flex: none;
  width: 56px;
  height: 56px;
  object-fit: cover;
  border-radius: 4px;
}
.tp-next-text {
  flex: 1;
  min-width: 0;
}
.tp .tp-next-label {
  margin: 0;
  font-size: 12px;
  color: var(--tp-muted);
}
.tp .tp-next-title {
  margin: 2px 0 0;
  font-family: var(--font-display);
  font-size: 17px;
  line-height: 1.35;
  color: var(--tp-paper);
}
.tp-next-actions {
  display: grid;
  gap: 6px;
}
.tp-next button {
  min-height: 32px;
  padding: 4px 12px;
  border: 1px solid var(--tp-line);
  border-radius: 999px;
  background: transparent;
  color: var(--tp-paper);
  font-size: 13px;
  cursor: pointer;
}
.tp-next .tp-next-go {
  background: var(--tp-paper);
  color: #080808;
  border-color: var(--tp-paper);
}
@media (max-width: 991px) {
  .tp-next {
    left: 16px;
    right: 16px;
    bottom: calc(16px + env(safe-area-inset-bottom, 0px));
    width: auto;
  }
  .tp-next.tp-next-raised {
    bottom: calc(76px + env(safe-area-inset-bottom, 0px));
  }
  .tp-embed {
    max-width: none;
  }
}
</style>
